import { Clock, MessageSquareText } from 'lucide-react';
import type { Task } from '@/types/api';
import { Markdown } from '@/components/Markdown';
import { Highlight, matchesReference } from '@/components/Highlight';
import { Avatar } from '@/app/layouts/Sidebar';

/**
 * All four, each in its own hue.
 *
 * The tint percentage falls with the priority as well as the colour: urgent is a
 * filled chip, low is barely there. Same shape everywhere, so the eye reads position
 * once and then only has to read colour.
 */
const PRIORITY: Record<Task['priority'], { label: string; color: string; tint: number }> = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)', tint: 18 },
  high: { label: 'High', color: 'var(--color-priority-high)', tint: 16 },
  normal: { label: 'Normal', color: 'var(--color-priority-normal)', tint: 13 },
  low: { label: 'Low', color: 'var(--color-priority-low)', tint: 12 },
};

function formatRemind(remindAt: string): { text: string; overdue: boolean } {
  const due = new Date(remindAt);
  const days = Math.round((due.getTime() - Date.now()) / 86_400_000);

  if (days < 0) return { text: days === -1 ? 'Yesterday' : `${Math.abs(days)}d overdue`, overdue: true };
  if (days === 0) return { text: 'Today', overdue: false };
  if (days === 1) return { text: 'Tomorrow', overdue: false };
  if (days < 7) return { text: `in ${days}d`, overdue: false };
  return { text: due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), overdue: false };
}

export function TaskCard({
  task,
  onOpen,
  showAssignee = false,
  highlight,
}: {
  task: Task;
  onOpen?: ((task: Task) => void) | undefined;
  /** Admin sees the whole company, so the card has to say whose it is. */
  showAssignee?: boolean;
  /** The search term this card came back for. Marks what matched. */
  highlight?: string | undefined;
}) {
  const remind = task.remindAt ? formatRemind(task.remindAt) : null;
  const priority = PRIORITY[task.priority];
  const done = task.status === 'completed' || task.status === 'cancelled';
  const { isNew, hasActivity } = task.unread;
  const referenceHit = Boolean(highlight && matchesReference(task.reference, highlight));

  return (
    /*
     * No status pill, and no priority stripe down the edge.
     *
     * The pill repeated the column the card was already sitting in — every card in
     * Pending said "Pending" — so it was a coloured chip carrying nothing, on every
     * card. The stripe was worse: a third vertical line beside the card border and the
     * column border, in a grey that read as decoration rather than as urgency.
     *
     * Urgency lives in the priority pill instead, in its own hue, where the colour is
     * attached to the word that means it.
     */
    <article
      className="rounded-xl border transition-shadow"
      style={{
        borderColor: isNew || hasActivity ? 'var(--accent)' : 'var(--border)',
        background: 'var(--surface)',
        boxShadow: 'var(--shadow-card)',
        // Near-free on long columns, and unlike windowing it breaks nothing.
        contentVisibility: 'auto',
        containIntrinsicSize: '0 110px',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen?.(task)}
        className="flex w-full flex-col gap-2 p-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-1.5">
          <span
            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium"
            style={{
              color: priority.color,
              background: `color-mix(in oklch, ${priority.color} ${priority.tint}%, transparent)`,
            }}
          >
            {priority.label}
          </span>

          {task.tag && (
            <span
              className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px]"
              style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
            >
              {task.tag}
            </span>
          )}

          {/*
            Two prompts, two marks. NEW is a card never opened — worth a word, the
            action is "read this". A DOT is one you have read that moved on since;
            the action is "catch up", which is quieter. Neither ever shows on someone
            else's card: the server returns unread empty for a non-assignee.
          */}
          {isNew ? (
            <span
              className="ml-auto inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
            >
              New
            </span>
          ) : (
            hasActivity && (
              <span
                className="ml-auto size-2 shrink-0 rounded-full"
                style={{ background: 'var(--accent)' }}
                title={`${task.unread.count} new`}
                aria-label={`${task.unread.count} updates since you last looked`}
              />
            )
          )}
        </span>

        <span
          className="line-clamp-2 text-sm font-medium leading-snug"
          style={done ? { color: 'var(--text-muted)' } : undefined}
        >
          {highlight ? <Highlight text={task.title} term={highlight} /> : task.title}
        </span>

        {task.description && (
          <span className="line-clamp-2 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {/* Inline: the preview is one flowing line, not a rendered document. */}
            <Markdown inline>{task.description}</Markdown>
          </span>
        )}

        {/* A hold has to say what it is waiting on — that is the whole point of it. */}
        {task.status === 'waiting' && task.waitingOn && (
          <span
            className="flex items-start gap-1.5 rounded-md px-2 py-1 text-[11px] leading-snug"
            style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
          >
            <Clock size={13} className="mt-px shrink-0" />
            <span>
              Waiting on <span style={{ color: 'var(--text)' }}>{task.waitingOn}</span>
            </span>
          </span>
        )}

        {/*
          One meta line, not a bordered footer. The reference had a whole divided row
          to itself, which cost a rule and ~28px on every card to show six characters.
        */}
        <span
          className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px]"
          style={{ color: 'var(--text-faint)' }}
        >
          {/* The reference is marked whole, not by substring: "444" and "s444" both
              mean S-444 without appearing inside it. */}
          <span className={`font-mono ${referenceHit ? 'search-hit rounded px-1' : ''}`}>
            {task.reference}
          </span>

          {remind && (
            <span
              className="flex items-center gap-1"
              style={remind.overdue ? { color: 'var(--color-priority-urgent)' } : undefined}
            >
              <Clock size={12} />
              {remind.text}
            </span>
          )}

          {task.noteCount > 0 && (
            <span className="flex items-center gap-1" title={`${task.noteCount} notes`}>
              <MessageSquareText size={12} />
              {task.noteCount}
            </span>
          )}

          {showAssignee && task.assignee && (
            <span className="ml-auto flex min-w-0 items-center gap-1.5">
              <Avatar
                name={task.assignee.name || task.assignee.email}
                url={task.assignee.avatarUrl}
                size={16}
              />
              <span className="truncate">{task.assignee.name || task.assignee.email}</span>
            </span>
          )}
        </span>
      </button>
    </article>
  );
}
